/**
 * Citation parsing, statute lookup, charge detection, and context-ranked
 * caselaw selection (all DOM-free).
 *
 * Faithful ports of parseCitation / lookupStatute / getDetectedCharges /
 * getCitationsForContext. `lookupStatute` takes the MGL citations map as an
 * argument instead of reading `window.mglSearch`.
 */

import type { ChargeSlug, CaselawEntry, MGLCitationsMap } from '@/types';
import { CASELAW } from '@/data/caselaw';

export interface ParsedCitation {
  chapter: string;
  section: string;
  /**
   * Optional subsection / paragraph path, outermost first. For
   * `c. 90, § 24(1)(a)(1)` this is `['1', 'a', '1']`. Absent when the citation
   * is only to the section.
   */
  subsections?: string[];
}

/** Build a ParsedCitation, uppercasing letter suffixes and dropping empty subs. */
function makeCitation(chapter: string, section: string, subs: string[]): ParsedCitation {
  const c: ParsedCitation = { chapter: chapter.toUpperCase(), section: section.toUpperCase() };
  if (subs.length) c.subsections = subs;
  return c;
}

/**
 * Parse a free-form statute citation into { chapter, section, subsections? }.
 *
 * Accepts, in order of preference:
 *   1. Dash shorthand — `90-24-1-a-1`, `90b-26a`, `265-1`, `265-13A`
 *      (chapter and section may carry a letter suffix; any further dash-
 *      separated tokens become the subsection/paragraph path).
 *   2. Prose / legal form — `G.L. c. 90B, § 26A(1)(a)`, `Chapter 265 Section 1`,
 *      `c.90 s.24` (parenthesized groups become the subsection path).
 *   3. Bare `265 1` (space-separated chapter + section).
 * Returns null if nothing parses.
 */
export function parseCitation(input: string): ParsedCitation | null {
  if (!input || typeof input !== 'string') return null;
  const raw = input.trim();
  if (!raw) return null;

  // 1) Dash shorthand: chapter-section[-sub[-sub...]], letters allowed as
  //    suffixes, and subsections may also be written in parentheses.
  //    Both notations are natural to type and both appear in practice:
  //      90-24-1-a-1   dash-separated
  //      265-13A(b)    parenthesised — this form previously failed to parse at
  //                    all, because the dash pattern's [0-9a-z] run cannot hold
  //                    brackets and the prose pattern requires a "c."/"chapter"
  //                    prefix, so the charge was stored as raw text with no
  //                    chapter, section, or subsection recorded anywhere.
  //      90-24(1)(a)   mixed
  //
  //    The trailing `(?=\s|$)` rather than `$` is what lets a DESCRIPTION follow
  //    the citation, which is the shape the docket importer writes:
  //
  //      265-1 MURDER
  //      265-15D STRANGULATION OR SUFFOCATION
  //      94C-32A POSSESSION WITH INTENT TO DISTRIBUTE CLASS B
  //
  //    Anchored to `$`, every one of those failed, so an imported charge showed
  //    no elements, no penalty and no case law — the Elements box just said
  //    "Unable to parse statute format". The prose branch below has always been
  //    unanchored and accepts a trailing description, so this was an
  //    inconsistency between the two branches rather than a missing feature.
  //
  //    The lookahead still requires the citation to END at whitespace, so
  //    "265-1MURDER" is rejected rather than read as section 1M.
  const short = raw.match(
    /^(\d{1,3}[a-z]?)-(\d{1,3}[a-z]?)((?:-[0-9a-z]+)*)((?:\(\s*[0-9A-Za-z½]+\s*\))*)(?=\s|$)/i
  );
  if (short) {
    const dashed = short[3] ? short[3].split('-').filter(Boolean) : [];
    const bracketed = [...short[4].matchAll(/\(\s*([0-9A-Za-z½]+)\s*\)/g)].map((m) => m[1]);
    return makeCitation(short[1], short[2], [...dashed, ...bracketed]);
  }

  // 2) Prose / legal form, with optional (x)(y)(z) subsections.
  const prose = raw.match(
    /c(?:hapter)?\.?\s*(\d{1,3}[A-Za-z]?)\b[\s,]*(?:§+|s(?:ec(?:tion)?)?\.?)\s*(\d{1,3}[A-Za-z]?)\b\s*((?:\(\s*[0-9A-Za-z½]+\s*\))*)/i
  );
  if (prose) {
    const subs = [...prose[3].matchAll(/\(\s*([0-9A-Za-z½]+)\s*\)/g)].map((m) => m[1]);
    return makeCitation(prose[1], prose[2], subs);
  }

  // 3) Bare "265 1" (space-separated chapter + section).
  const spaced = raw.match(/^(\d{1,3}[A-Za-z]?)\s+(\d{1,3}[A-Za-z]?)$/);
  if (spaced) return makeCitation(spaced[1], spaced[2], []);

  return null;
}

/** Format a parsed citation back to canonical form, e.g. `c. 90, § 24(1)(a)(1)`. */
export function formatCitation(p: ParsedCitation): string {
  const subs = (p.subsections || []).map((s) => `(${s})`).join('');
  return `c. ${p.chapter}, § ${p.section}${subs}`;
}

export interface StatuteLookup {
  chapter: string;
  section: string;
  heading: string;
  url: string;
  citation: string;
}

/** Look up a statute in the MGL citations map, trying several citation formats. */
export function lookupStatute(
  chapter: string,
  section: string,
  citations: MGLCitationsMap
): StatuteLookup | null {
  if (!citations) return null;

  const formats = [
    `G.L. c. ${chapter}, § ${section}`,
    `G.L. c. ${chapter}, § ${section} `,
    `G.L. C. ${chapter}, § ${section}`,
    `G.L. c. ${chapter.padStart(3, '0')}, § ${section}`
  ];

  for (const format of formats) {
    const c = citations[format];
    if (c) {
      return { chapter: c.c, section: c.s, heading: c.h, url: c.u, citation: format };
    }
  }

  return null;
}

/**
 * Every piece of charge text the user has entered, as one string for charge
 * detection.
 *
 * Charges live in two places: `chargeList` (committed via "Add Charge") and
 * `chargeDescription` (the box, holding whatever is typed but not yet added).
 * Detection used to read ONLY the box — but addChargeToList() clears the box
 * when it commits a charge, so the moment a user followed the intended
 * Look up -> Add Charge flow, the box went empty and detection returned
 * nothing. Juror scoring, voir dire question selection, the Law tab filter,
 * and the "Charges:" line of every printed document all silently saw a case
 * with no charges. Nothing errored; the charge-specific scoring this app
 * exists to do just quietly stopped applying.
 *
 * Reading both keeps a charge counted once it is committed and still picks up
 * text that has been typed but not yet added.
 */
export function allChargeText(
  chargeList: ReadonlyArray<{ text: string }> | undefined,
  chargeDescription: string | undefined
): string {
  const listed = (chargeList ?? []).map((c) => c.text).join('\n');
  return [listed, chargeDescription ?? ''].filter(Boolean).join('\n');
}

/** Detect charge slugs from free-text charge description (faithful regexes). */
export function getDetectedCharges(chargeDescription: string): ChargeSlug[] {
  const txt = (chargeDescription || '').toLowerCase();
  const found: ChargeSlug[] = [];
  const add = (k: ChargeSlug): void => {
    if (found.indexOf(k) === -1) found.push(k);
  };
  if (/murder|homicide|c\.?\s*265\s*,?\s*(s\.?|section)?\s*1\b|manslaughter/.test(txt)) add('murder');
  if (/drug|narcotic|controlled substance|c\.?\s*94\s*c|possession with intent|trafficking/.test(txt)) add('drug');
  if (/fraud|larceny|embezzle|forgery|c\.?\s*266/.test(txt)) add('fraud');
  if (/assault|battery|a&b|c\.?\s*265\s*,?\s*(s\.?|section)?\s*13/.test(txt)) add('assault');
  if (/\boui\b|\bdui\b|operating under the influence|c\.?\s*90\s*,?\s*(s\.?|section)?\s*24/.test(txt)) add('oui');
  if (/sexual assault|rape|indecent assault|c\.?\s*265\s*,?\s*(s\.?|section)?\s*22/.test(txt)) add('sexual');
  return found;
}

/**
 * Rank the CASELAW database against the active charges and a context string.
 * Charge-slug match = +5, 'all' = +2, each topic keyword found in the text = +1.
 * Returns entries with score > 0, the 12 most relevant.
 */
export function getCitationsForContext(
  charges: ChargeSlug[],
  contextText: string
): CaselawEntry[] {
  const text = (contextText || '').toLowerCase();
  const activeCharges = Array.isArray(charges) ? charges : [];
  const scored = CASELAW.map((c) => {
    let score = 0;
    if (c.charges === 'all') score += 2;
    else if (Array.isArray(c.charges) && c.charges.some((ch) => activeCharges.indexOf(ch) > -1)) {
      score += 5;
    }
    (c.topics || []).forEach((t) => {
      if (text.indexOf(t.toLowerCase()) > -1) score += 1;
    });
    return { c, score };
  });
  scored.sort((a, b) => b.score - a.score);
  return scored
    .filter((x) => x.score > 0)
    .slice(0, 12)
    .map((x) => x.c);
}
